import type { CSSProperties } from "react";
import { LucideIcon } from "../FG/LucideIcon";

/**
 * Tri-state preference card. Three synchronized controls sharing one model:
 *
 * • Center (title + hint): cycles neutral → love → avoid → neutral
 * • ♥: selects love — tap again while liked clears to neutral; from dislike jumps straight to liked
 * • ✕: selects dislike — tap again while disliked clears to neutral; from liked jumps straight to disliked
 *
 * Icons are sibling buttons alongside the strip (no nesting), so taps never double-fire against the cyclers.
 *
 * Maps to paired prefer/exclude lists: `love` → `preferred*`, `avoid` → `excluded*`,
 * `neutral` → neither.
 */
export type PrefState = "neutral" | "love" | "avoid";

function cycleState(current: PrefState): PrefState {
  if (current === "neutral") return "love";
  if (current === "love") return "avoid";
  return "neutral";
}

/** ♥ chooses the “like” pole: loved → neutral → loved; dislike → jumped to loved. */
function applyLoveShortcut(current: PrefState): PrefState {
  if (current === "love") return "neutral";
  return "love";
}

/** ✕ chooses the “avoid” pole: avoided → neutral → avoided; liked → jumped to avoided. */
function applyAvoidShortcut(current: PrefState): PrefState {
  if (current === "avoid") return "neutral";
  return "avoid";
}

export function PrefCard({
  label,
  hint,
  value,
  onChange
}: {
  label: string;
  hint?: string;
  value: PrefState;
  onChange: (next: PrefState) => void;
}) {
  const bg =
    value === "love"
      ? "var(--fg-lime-pale)"
      : value === "avoid"
        ? "var(--fg-purple-50)"
        : "#fff";
  const border =
    value === "love"
      ? "var(--fg-lime-deep)"
      : value === "avoid"
        ? "var(--fg-purple)"
        : "var(--fg-gray-200)";
  const labelColor = value === "avoid" ? "var(--fg-purple)" : "var(--fg-ink)";
  const labelDeco = value === "avoid" ? "line-through" : "none";

  const heartIcon: CSSProperties = {
    appearance: "none",
    cursor: "pointer",
    width: 32,
    height: 32,
    borderRadius: 'var(--fg-r-pill)',
    flexShrink: 0,
    border:
      value === "love"
        ? "1px solid var(--fg-lime-deep)"
        : "1px solid var(--fg-gray-200)",
    background: value === "love" ? "var(--fg-lime-deep)" : "#fff",
    color: value === "love" ? "#fff" : "var(--fg-gray-500)",
    display: "inline-grid",
    placeItems: "center",
    fontSize: 14,
    lineHeight: 1
  };

  const avoidIcon: CSSProperties = {
    appearance: "none",
    cursor: "pointer",
    width: 32,
    height: 32,
    borderRadius: 'var(--fg-r-pill)',
    flexShrink: 0,
    border:
      value === "avoid" ? "1px solid var(--fg-danger)" : "1px solid var(--fg-gray-200)",
    background: value === "avoid" ? "var(--fg-danger)" : "#fff",
    color: value === "avoid" ? "#fff" : "var(--fg-gray-500)",
    display: "inline-grid",
    placeItems: "center",
    fontSize: 13,
    lineHeight: 1
  };

  function stateWord(): string {
    if (value === "love") return "liked";
    if (value === "avoid") return "disliked";
    return "neutral";
  }

  return (
    <div
      role="presentation"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "10px 10px",
        borderRadius: 'var(--fg-r-sm)',
        background: bg,
        border: `1px solid ${border}`,
        transition: "all .12s",
        boxSizing: "border-box"
      }}>
      <button
        type="button"
        onClick={() => onChange(applyLoveShortcut(value))}
        aria-label={value === "love" ? `Clear preference for ${label}` : `Mark ${label} as liked`}
        aria-pressed={value === "love"}
        title="Like"
        style={heartIcon}>
        <LucideIcon name="heart" size={16} />
      </button>
      <button
        type="button"
        onClick={() => onChange(cycleState(value))}
        aria-label={`${label}. ${hint ? `${hint}. ` : ""}Preference is ${stateWord()}. Activate to cycle: like, dislike, then clear.`}
        style={{
          flex: 1,
          minWidth: 0,
          cursor: "pointer",
          textAlign: "center",
          padding: "2px 4px",
          margin: "-2px -4px",
          borderRadius: 'var(--fg-r-sm)',
          border: "none",
          background: "transparent",
          appearance: "none",
          WebkitAppearance: "none",
          font: "inherit",
          display: "block"
        }}>
        <div
          style={{
            fontFamily: "var(--fg-display)",
            fontWeight: 600,
            fontSize: 14,
            color: labelColor,
            textDecoration: labelDeco,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
          {label}
        </div>
        {hint && (
          <div
            className="fg-caption"
            style={{ marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {hint}
          </div>
        )}
      </button>
      <button
        type="button"
        onClick={() => onChange(applyAvoidShortcut(value))}
        aria-label={
          value === "avoid" ? `Clear dislike for ${label}` : `Mark ${label} as disliked`
        }
        aria-pressed={value === "avoid"}
        title="Dislike"
        style={avoidIcon}>
        <LucideIcon name="thumbs-down" size={16} />
      </button>
    </div>
  );
}
